// The chrome half of a look (themes.md §3.4, §12, R814, R823).
//
// Three fields of the World body say how the DOM chrome behaves: the
// notices column, the chrome dock-in and the season banner. They are not
// canvas marks and they are not colours, so they reach their modules two
// ways at once:
//
//  - **As a class on `#ui`.** `wearChromeLook` writes `theme-notices-<id>`,
//    `theme-dock-<id>` and `theme-banner-<id>` on the chrome root, and
//    `ui/styles.css` selects on them. A look that only changes CSS needs no
//    TypeScript at all.
//  - **As an id in the module.** `NoticeColumn`, `ChromeDockIn` and
//    `SeasonBanner` each take their own id, because a value can also change
//    the markup a module writes or the clock it runs on.
//
// This module holds the pair, so the two can never name different values.
// It draws nothing itself.
//
// **The AUTO look is the shipped look.** Every field of `AUTO_CHROME_LOOK`
// is the field of the vendored AUTO World theme, which is position 0 of
// each generated list (R822). A look that names an id the build does not
// carry falls back to AUTO, field by field, the way the resolver reads a
// custom body (§12).

import {
  BANNER_STYLES, DOCK_STYLES, NOTICE_STYLES,
} from '../gen/balance';
import { VENDORED_THEMES } from '../gen/presentation';
import type { BannerStyle, DockStyle, NoticeStyle } from './settings';
import type { Look } from './look';
import type { PreviewLook } from '../preview/look';

/** What the DOM chrome takes from a look. */
export interface ChromeLook {
  /** The notices column and the drain line (R430). */
  notices: NoticeStyle;
  /** The chrome dock-in (R182). */
  dock: DockStyle;
  /** The season banner (R184). */
  banner: BannerStyle;
}

/** The vendored AUTO World theme (themes.md §4.1). */
const AUTO_WORLD = VENDORED_THEMES.world[0];

/** Today's chrome: the three fields of the AUTO World theme. */
export const AUTO_CHROME_LOOK: ChromeLook = {
  notices: AUTO_WORLD.notices,
  dock: AUTO_WORLD.dock,
  banner: AUTO_WORLD.banner,
};

/** The ids each field may take, in the order of the World body. */
const LISTS = {
  notices: NOTICE_STYLES,
  dock: DOCK_STYLES,
  banner: BANNER_STYLES,
} as const;

/** The three field names, for a caller that walks them. */
export const CHROME_FIELDS = ['notices', 'dock', 'banner'] as const;

/**
 * The chrome look of a resolved look or of a preview look.
 *
 * A `Look` carries the three fields inside its World body; a `PreviewLook`
 * carries them flat, and leaves one out where the caller named no value. A
 * field that is missing, or that names an id this build does not carry,
 * takes the AUTO value of that field.
 */
export function chromeLookOf(look: Look | PreviewLook): ChromeLook {
  const src: Partial<Record<keyof ChromeLook, string>> = 'world' in look ? look.world : look;
  const out = { ...AUTO_CHROME_LOOK };
  for (const f of CHROME_FIELDS) {
    const id = src[f];
    if (typeof id === 'string' && (LISTS[f] as readonly string[]).includes(id)) {
      (out as Record<string, string>)[f] = id;
    }
  }
  return out;
}

/**
 * Puts the chrome look on a chrome root, as one class per field.
 *
 * The call is idempotent and it touches no other class: it removes the
 * class of the field it writes and leaves everything else — `sheet`, the
 * dock-in's own `docking` and `snapping`, the film's class — where it is.
 * The preview calls it on every `show`, and the game calls it one time,
 * because a theme cannot change during a match (themes.md §2).
 */
export function wearChromeLook(root: HTMLElement, look: ChromeLook): void {
  for (const f of CHROME_FIELDS) {
    const want = `theme-${f}-${look[f]}`;
    const stale = `theme-${f}-`;
    for (const cls of Array.from(root.classList)) {
      if (cls !== want && cls.startsWith(stale)) root.classList.remove(cls);
    }
    root.classList.add(want);
  }
}
